import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import api from "../utils/api";
import { buildMachineColorMap } from "../utils/machineColors";
import { getClientMachines } from "../controllers/machineController";

const historicalCache = new Map();

const MODES = {
  minute: {
    label: "Minute",
    granularity: "minute",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  hour: {
    label: "Hour",
    granularity: "hour",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },

  day: {
    label: "Day",
    granularity: "day",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  month: {
    label: "Month",
    granularity: "month",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  year: {
    label: "Year",
    granularity: "year",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  second: {
    label: "Second",
    granularity: "second",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
};

export const useHistoricalData = () => {
  const [mode, setMode] = useState("hour");
  const [selectedMetric, setSelectedMetric] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [avgMonthlyPfData, setAvgMonthlyPfData] = useState(null);
  const [avgMonthlyPfLoading, setAvgMonthlyPfLoading] = useState(false);
  const [mdiData, setMdiData] = useState(null);
  const [mdiLoading, setMdiLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [machineColors, setMachineColors] = useState({});
  const [selectedMachineNode, setSelectedMachineNode] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1); // Yesterday
    end.setHours(23, 59, 59, 999);

    const start = new Date(end);
    start.setDate(end.getDate() - 6); // 7 days inclusive of yesterday (start to end = 7 days)
    start.setHours(0, 0, 0, 0);

    return { start, end };
  });

  // Reset fetch deduplication when mode changes
  useEffect(() => {
    // When mode changes, force a fresh fetch by clearing lastFetchParams
    lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null };
    console.log(
      "[Effect] Mode changed to:",
      mode,
      "- clearing deduplication cache",
    );
  }, [mode]);

  const abortControllerRef = useRef(null);
  const lastFetchParams = useRef({ mode: null, start: null, end: null, selected: null, metric: null });
  const lastSummaryFetchParams = useRef({ machineName: null, start: null, end: null, metric: null, mode: null });

  const [graphDateRange, setGraphDateRange] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1); // Yesterday
    end.setHours(23, 59, 59, 999);

    const start = new Date(end);
    start.setDate(end.getDate() - 6); // 7 days inclusive of yesterday
    start.setHours(0, 0, 0, 0);

    return { start, end };
  });

  const username = useMemo(() => localStorage.getItem("username") || "", []);

  const modeMeta = MODES[mode] || MODES.day;


  const selectedMetricRef = useRef(selectedMetric);
  useEffect(() => {
    selectedMetricRef.current = selectedMetric;
  }, [selectedMetric]);

  const fetchData = useCallback(async (selectedMachinesParam, metricParam) => {
    if (!username) {
      setError("Missing username. Please log in again.");
      return;
    }

    // Guard: second mode max 24h; minute mode max 24h sliding window
    if (mode === "second" || mode === "minute") {
      const diffMs = Math.abs(
        graphDateRange.end.getTime() - graphDateRange.start.getTime(),
      );
      const maxSpanMs = 24 * 60 * 60 * 1000 + 60000;
      if (diffMs > maxSpanMs) {
        console.log(
          `Skipping fetchData: ${mode} mode range > 24h`,
          diffMs,
        );
        return;
      }
    }

    let currentController;
    try {
      const startStr = formatDate(graphDateRange.start);
      const endStr = formatDate(graphDateRange.end);

      console.log("[useHistoricalData] fetchData called", { selectedMachinesParam, metricParam, mode, start: startStr, end: endStr });

      // Determine resolved metric before dedup check so metric is considered
      const currentSelectedMetric = selectedMetricRef.current;
      const resolvedMetric = metricParam
        || (currentSelectedMetric ? currentSelectedMetric.split("_").slice(1).join("_") : null)
        || "power_kw";

      // Deduplication check - include selected machines and metric so UI
      // changes to machine/metric still trigger a fetch even when date/mode unchanged.
      const selKey = selectedMachinesParam || null;
      const metricKey = resolvedMetric || null;

      if (
        lastFetchParams.current.mode === mode &&
        lastFetchParams.current.start === startStr &&
        lastFetchParams.current.end === endStr &&
        lastFetchParams.current.selected === selKey &&
        lastFetchParams.current.metric === metricKey
      ) {
        console.log("Skipping redundant fetch for:", { mode, start: startStr, selected: selKey, metric: metricKey });
        return;
      }

      // Check if only the machine or metric changed, while date and mode remained the same
      const isOnlyMachineOrMetricChange =
        lastFetchParams.current.mode === mode &&
        lastFetchParams.current.start === startStr &&
        lastFetchParams.current.end === endStr;

      // Update LAST PARAMS early to prevent rapid second calls from aborting this one
      lastFetchParams.current = { mode, start: startStr, end: endStr, selected: selKey, metric: metricKey };

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      currentController = new AbortController();
      abortControllerRef.current = currentController;

      if (!isOnlyMachineOrMetricChange || !rawResponse) {
        setLoading(true);
      }
      setSummaryLoading(true);
      setAvgMonthlyPfLoading(true);
      setMdiLoading(true);
      setError(null);

      const targetMachine =
        selectedMachinesParam && selectedMachinesParam !== "ALL" && selectedMachinesParam !== "__ALL_MACHINES__"
          ? selectedMachinesParam
          : selectedMachineNode?.name || "Usage";

      const params = new URLSearchParams({
        username,
        mode,
        start: startStr,
        end: endStr,
        machine_name: targetMachine,
        selected_machines: "ALL,Usage",
      });

      // Metric filter applied to API to return only requested metric series
      if (resolvedMetric) params.append("metric", resolvedMetric);

      const queryString = params.toString();
      let bundle;

      if (historicalCache.has(queryString)) {
        bundle = historicalCache.get(queryString);
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        // Longer timeout for dense series (second / up to 24h of minutes)
        const timeout =
          mode === "second" || mode === "minute" ? 90000 : 60000;

        const response = await api.get(`/historical_bundle?${queryString}`, {
          signal: currentController.signal,
          timeout,
        });
        bundle = response.data;
        historicalCache.set(queryString, bundle);
      }

      const resData = bundle?.data?.data || bundle?.data || bundle;
      setRawResponse(resData || null);

      setSummaryData(bundle?.summary || null);
      setAvgMonthlyPfData(bundle?.lpf || null);
      setMdiData(bundle?.mdi || null);

      // Prefer `Hierarchy` for building the machine selector (full list of machines).
      if (resData?.Hierarchy) {
        const parsedTree = parseHierarchy(resData.Hierarchy);
        setHierarchyData(parsedTree);

        // Build machine colors if not yet built
        const hierarchyColorMap = {};
        Object.entries(resData.Hierarchy).forEach(([name, meta]) => {
          if (meta && typeof meta === "object" && meta.color) {
            hierarchyColorMap[name] = meta.color;
          }
        });

        getClientMachines(username)
          .then((clientData) => {
            const clientMachines = clientData?.machines || [];
            const clientMachineNames = new Set(clientMachines.map((m) => m.machine_name || m.name));
            const hierarchyOnlyNodes = Object.entries(resData.Hierarchy)
              .filter(([name]) => !clientMachineNames.has(name))
              .map(([name, meta]) => ({
                machine_name: name,
                color: (meta && typeof meta === "object") ? meta.color : null,
              }));
            const allNodes = [...clientMachines, ...hierarchyOnlyNodes];
            const fullColorMap = buildMachineColorMap(allNodes, hierarchyColorMap);
            setMachineColors((prevColors) => {
              if (Object.keys(prevColors).length > 0) return prevColors;
              return fullColorMap;
            });
          })
          .catch(() => { });
      }
    } catch (err) {
      if (
        err.name === "AbortError" ||
        err.message === "canceled" ||
        err.code === "ERR_CANCELED"
      ) {
        console.log("Fetch aborted/canceled - ignoring error state update");
        return;
      }
      console.error("Fetch error:", err);
      // Reset lastFetchParams on error to allow retries
      lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null };
      setError(err.message || "Unable to load data");
    } finally {
      if (currentController && !currentController.signal.aborted) {
        setLoading(false);
        setSummaryLoading(false);
        setAvgMonthlyPfLoading(false);
        setMdiLoading(false);
      }
    }
  }, [username, mode, graphDateRange.start, graphDateRange.end, selectedMachineNode?.name]);

  const prevFetchRef = useRef({ start: null, end: null, mode: null, selected: null, metric: null });

  useEffect(() => {
    const startTs = graphDateRange.start ? graphDateRange.start.getTime() : null;
    const endTs = graphDateRange.end ? graphDateRange.end.getTime() : null;

    const sel = selectedMachineNode?.name || "Usage";
    const metricParam = selectedMetricRef.current
      ? selectedMetricRef.current.split("_").slice(1).join("_")
      : "power_kw";

    if (
      prevFetchRef.current.start === startTs &&
      prevFetchRef.current.end === endTs &&
      prevFetchRef.current.mode === mode &&
      prevFetchRef.current.selected === sel &&
      prevFetchRef.current.metric === metricParam
    ) {
      return;
    }

    prevFetchRef.current = { start: startTs, end: endTs, mode, selected: sel, metric: metricParam };
    fetchData(sel, metricParam);
  }, [graphDateRange.start, graphDateRange.end, mode, selectedMachineNode, fetchData]);

  const fetchSummaryData = useCallback(
    (machineName, comparisonMetric) => {
      if (!username || !machineName) return;
      const metricMapping = {
        power_kw: "power_kw",
        voltage: "voltage",
        current: "current",
        power_factor: "power_factor",
        frequency: "frequency",
        THD_V: "thd_voltage",
        THD_I: "thd_current",
        TAP: "tap",
        energy: "energy",
        thd_voltage: "thd_voltage",
        thd_current: "thd_current",
        tap: "tap",
      };
      const apiMetric = metricMapping[comparisonMetric] || "power_kw";
      fetchData(machineName, apiMetric);
    },
    [username, fetchData],
  );



  // Set initial range when mode changes (layout phase so graphDateRange is
  // narrowed before the fetch useEffect runs; avoids minute/second guard skip
  // while graphDateRange still reflects the previous mode's wide span)
  const lastModeRef = useRef(mode);

  useLayoutEffect(() => {
    const modeChanged = lastModeRef.current !== mode;
    if (!modeChanged) return;

    const previousMode = lastModeRef.current;
    lastModeRef.current = mode;

    const end = new Date();
    if (mode !== "second") {
      end.setSeconds(0, 0);
    }

    if (mode === "second") {
      // For second mode: always reset to 1 hour back from current time
      const start = new Date(end.getTime() - 60 * 60 * 1000); // 1 hour
      console.log("[Mode Change] Second mode - setting 1-hour range", {
        start: formatDate(start),
        end: formatDate(end),
      });
      setGraphDateRange({ start, end });
    } else if (mode === "minute") {
      // Sliding window: last 24 hours ending at current time (minute resolution)
      const rangeEnd = new Date(end);
      rangeEnd.setSeconds(0, 0);
      const rangeStart = new Date(
        rangeEnd.getTime() - 24 * 60 * 60 * 1000,
      );
      console.log("[Mode Change] Minute mode - 24h sliding window", {
        start: formatDate(rangeStart),
        end: formatDate(rangeEnd),
      });
      setGraphDateRange({ start: rangeStart, end: rangeEnd });
      setDateRange({ start: rangeStart, end: rangeEnd });
    } else {
      // For hour/day/month/year modes
      // If coming from minute or second mode, reset to 7 days
      if (previousMode === "minute" || previousMode === "second") {
        const { start: rStart, end: rEnd } = getRelativeRange(7);
        console.log("[Mode Change] Switching from minute/second to", mode, {
          start: formatDate(rStart),
          end: formatDate(rEnd),
        });
        setDateRange({ start: rStart, end: rEnd });
        setGraphDateRange({ start: rStart, end: rEnd });
      } else {
        // Otherwise, sync graphDateRange with current dateRange
        console.log("[Mode Change]", mode, "- syncing with dateRange");
        setGraphDateRange({ start: dateRange.start, end: dateRange.end });
      }
    }
  }, [mode, dateRange.start, dateRange.end]);

  // Sync graphDateRange when dateRange changes in non-isolated modes
  useEffect(() => {
    if (mode !== "minute" && mode !== "second") {
      setGraphDateRange({ start: dateRange.start, end: dateRange.end });
    }
  }, [dateRange, mode]);

  const setGraphDateRangeFromStrings = ({ start, end }) => {
    const parsedStart = start ? new Date(start) : null;
    const parsedEnd = end ? new Date(end) : null;
    if (
      !parsedStart ||
      !parsedEnd ||
      Number.isNaN(parsedStart) ||
      Number.isNaN(parsedEnd)
    )
      return;
    // Logical check: start <= end
    if (parsedStart > parsedEnd) return;

    const commitGraph = (s, e) => {
      setGraphDateRange({ start: s, end: e });
      if (mode === "minute") {
        setDateRange({ start: s, end: e });
      }
    };

    // Minute mode: max span 24 hours (can cross calendar midnight)
    if (mode === "minute") {
      const diffMs = parsedEnd.getTime() - parsedStart.getTime();
      const maxSpanMs = 24 * 60 * 60 * 1000;
      if (diffMs > maxSpanMs) {
        const adjustedEnd = new Date(parsedStart.getTime() + maxSpanMs);
        commitGraph(parsedStart, adjustedEnd);
        return;
      }
    }

    // For second mode, ensure range is within 1 hour
    if (mode === "second") {
      const diffMs = parsedEnd.getTime() - parsedStart.getTime();
      const oneHourMs = 60 * 60 * 1000;
      if (diffMs > oneHourMs) {
        // Adjust end to be 1 hour from start
        const adjustedEnd = new Date(parsedStart.getTime() + oneHourMs);
        setGraphDateRange({ start: parsedStart, end: adjustedEnd });
        return;
      }
    }

    commitGraph(parsedStart, parsedEnd);
  };

  const timeLabels = useMemo(() => {
    const source = rawResponse?.["Date & Time"];
    if (!source) return [];
    if (Array.isArray(source)) return source;
    if (typeof source === "object") {
      return Object.values(source).filter(Boolean);
    }
    return [source];
  }, [rawResponse]);

  // Derived indices after DateTime filtering
  const filteredIndices = useMemo(() => {
    const indices = [];
    const startTimeToken = graphDateRange.start.getTime();
    const endTimeToken = graphDateRange.end.getTime();

    // Buffer to handle slight mismatches
    const buffer = 5000;

    for (let i = 0; i < timeLabels.length; i++) {
      const label = timeLabels[i];
      if (!label) continue;

      const date = new Date(label);
      if (Number.isNaN(date.getTime())) {
        // ⚠️ CRITICAL: Skip invalid dates to prevent ApexCharts "parser Error"
        continue;
      }

      const ts = date.getTime();
      if (ts >= startTimeToken - buffer && ts <= endTimeToken + buffer) {
        indices.push(i);
      }
    }

    return indices;
  }, [timeLabels, graphDateRange]);

  // Unified indices after decimation
  const decimatedIndices = useMemo(() => {
    if (!filteredIndices.length) return [];

    const MAX_POINTS = 2000;
    if (filteredIndices.length <= MAX_POINTS) return filteredIndices;

    const step = Math.ceil(filteredIndices.length / MAX_POINTS);
    return filteredIndices.filter((_, idx) => idx % step === 0);
  }, [filteredIndices]);

  const seriesByMetric = useMemo(() => {
    if (!rawResponse) return {};
    const result = {};
    const usageTotals = [];

    const processValues = (rawValues) => {
      let values = rawValues;
      if (!Array.isArray(values)) {
        if (values && typeof values === "object") {
          values = Object.values(values);
        } else if (values !== undefined && values !== null) {
          values = [values];
        } else {
          values = [];
        }
      }
      return values.map((value) => {
        if (typeof value === "number") return value;
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      });
    };

    // Determine which machines contribute to Usage
    const hasGrid = hierarchyData && Array.isArray(hierarchyData) && hierarchyData.some(
      (node) => (node.category || "").toUpperCase() === "GRID"
    );

    const machinesToSum = new Set();
    if (hasGrid) {
      hierarchyData.forEach((node) => {
        if ((node.category || "").toUpperCase() === "GRID") {
          machinesToSum.add(node.name);
        }
      });
    } else if (hierarchyData && Array.isArray(hierarchyData)) {
      hierarchyData.forEach((node) => {
        if ((node.category || "").toUpperCase() === "MACHINE") {
          machinesToSum.add(node.name);
        }
      });
    }

    // Handle New Nested Structure
    if (rawResponse.machines) {
      Object.entries(rawResponse.machines).forEach(([id, machine]) => {
        const machineName = machine.name || id;
        if (machine.data) {
          Object.entries(machine.data).forEach(([metricName, rawValues]) => {
            const compositeKey = `${machineName}_${metricName}`;
            const parsedValues = processValues(rawValues);

            // For Usage aggregate we only want the positive power series (kW)
            // Accept backend variants like `power_kw` or `kW`, but explicitly
            // exclude negative/offset series such as `kW_neg`.
            const isPowerKw = /^power[_-]?kw$/i.test(metricName) || /^kw(?!h)$/i.test(metricName) || /^\[kw\]$/i.test(metricName);
            const isNeg = /neg/i.test(metricName) || /_neg$/i.test(metricName);
            if (machinesToSum.has(machineName) && isPowerKw && !isNeg) {
              parsedValues.forEach((value, index) => {
                if (value == null) return;
                usageTotals[index] = (usageTotals[index] || 0) + value;
              });
            }

            // Apply filtering and decimation using the unified decimatedIndices
            const filteredValues = decimatedIndices
              .filter((idx) => idx < parsedValues.length)
              .map((idx) => parsedValues[idx]);

            result[compositeKey] = filteredValues;
          });
        }
      });
    } else {
      // Legacy structure handling
      Object.entries(rawResponse).forEach(([key, rawValues]) => {
        if (key === "Date & Time") return;
        const parsedValues = processValues(rawValues);

        // Apply filtering and decimation using the unified decimatedIndices
        const filteredValues = decimatedIndices
          .filter((idx) => idx < parsedValues.length)
          .map((idx) => parsedValues[idx]);

        result[key] = filteredValues;
      });
    }

    if (usageTotals.length && !result["Usage_[kW]"] && !result["Usage_power_kw"] && !result["Usage_kW"]) {
      result.Usage_power_kw = decimatedIndices
        .filter((idx) => idx < usageTotals.length)
        .map((idx) => usageTotals[idx]);
    }

    return result;
  }, [rawResponse, decimatedIndices, hierarchyData]);

  const metrics = useMemo(() => Object.keys(seriesByMetric), [seriesByMetric]);

  useEffect(() => {
    if (!metrics.length) {
      if (selectedMetric !== "") {
        setSelectedMetric("");
      }
      return;
    }
    if (!selectedMetric || !metrics.includes(selectedMetric)) {
      const usageKey = metrics.find((m) => m.startsWith("Usage_"));
      if (usageKey) {
        setSelectedMetric(usageKey);
        return;
      }
      setSelectedMetric(metrics[0]);
    }
  }, [metrics, selectedMetric]);

  const displayLabels = useMemo(() => {
    if (!timeLabels.length) return [];

    // Apply decimation to labels using the unified decimatedIndices
    return decimatedIndices.map((idx) =>
      formatDisplayLabel(timeLabels[idx], mode),
    );
  }, [timeLabels, decimatedIndices, mode]);

  const chartData = useMemo(() => {
    if (!displayLabels.length || !selectedMetric) {
      return { labels: [], data: [] };
    }
    return {
      labels: displayLabels,
      data: seriesByMetric[selectedMetric] || [],
    };
  }, [displayLabels, selectedMetric, seriesByMetric]);

  const stats = useMemo(() => {
    if (!chartData.data.length) return null;
    const numbers = chartData.data
      .map((value) => (typeof value === "number" ? value : parseFloat(value)))
      .filter((value) => Number.isFinite(value));

    if (!numbers.length) return null;

    const sum = numbers.reduce((acc, val) => acc + val, 0);
    const avg = sum / numbers.length;
    const min = Math.min(...numbers);
    const max = Math.max(...numbers);
    const delta = numbers[numbers.length - 1] - numbers[0];

    return {
      average: avg,
      min,
      max,
      latest: numbers[numbers.length - 1],
      change: delta,
    };
  }, [chartData.data]);

  const setDateRangeFromStrings = ({ start, end }) => {
    const parsedStart = start ? new Date(start) : null;
    const parsedEnd = end ? new Date(end) : null;
    if (
      !parsedStart ||
      !parsedEnd ||
      Number.isNaN(parsedStart) ||
      Number.isNaN(parsedEnd)
    )
      return;
    // Logical check: start <= end
    if (parsedStart > parsedEnd) return;
    setDateRange({ start: parsedStart, end: parsedEnd });
  };

  const applyQuickRange = (days) => {
    setDateRange(getRelativeRange(days));
  };

  /** Reset the fetch dedup cache so the next fetchData/refetch call always hits the API. */
  const clearFetchCache = useCallback(() => {
    lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null };
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null };
  }, []);

  /** Reset the summary dedup cache so the next fetchSummaryData call always hits the API.
   * Call this whenever the selected machine changes to avoid stale-cache skips. */
  const clearSummaryCache = useCallback(() => {
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null };
  }, []);

  return {
    mode,
    setMode,
    dateRange,
    graphDateRange,
    setDateRange,
    setDateRangeFromStrings,
    setGraphDateRangeFromStrings,
    selectedMetric,
    setSelectedMetric,
    loading,
    error,
    chartData,
    metrics,
    seriesByMetric,
    modeMeta,
    stats,
    rawLabels: useMemo(() => {
      if (!timeLabels.length) return [];
      return decimatedIndices.map((idx) => timeLabels[idx]);
    }, [timeLabels, decimatedIndices]),
    lastTimestamp: chartData.labels.at?.(-1) || null,
    applyQuickRange,
    refetch: fetchData,
    fetchSummaryData,
    clearSummaryCache,
    clearFetchCache,
    summaryData,
    summaryLoading,
    avgMonthlyPfData,
    avgMonthlyPfLoading,
    mdiData,
    mdiLoading,
    rawResponse,
    machinesMdiData: mdiData || rawResponse?.mdi || null,
    hasData: Boolean(chartData.labels.length && chartData.data.length),
    hierarchyData,
    machineColors,
    selectedMachineNode,
    setSelectedMachineNode,
  };
};

export const getRelativeRange = (days) => {
  const end = new Date();

  // Base logic for historical ranges (exclude today)
  if (days === 1 || days === 3 || days === 7 || days === 30) {
    end.setDate(end.getDate() - 1);
  }

  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);

  return { start, end };
};

const formatDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatDisplayLabel = (rawLabel, mode) => {
  if (!rawLabel) return "";
  const parsed = new Date(rawLabel);
  // Check if date is invalid
  if (Number.isNaN(parsed.getTime())) return rawLabel;
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dayName = days[parsed.getDay()];
  const monthNum = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const monthName = months[parsed.getMonth()];
  const day = `${parsed.getDate()}`.padStart(2, "0");
  const year = parsed.getFullYear().toString().slice(-2);
  const fullYear = parsed.getFullYear();
  const hours = `${parsed.getHours()}`.padStart(2, "0");
  const minutes = `${parsed.getMinutes()}`.padStart(2, "0");

  if (mode === "second") {
    const seconds = `${parsed.getSeconds()}`.padStart(2, "0");
    return `${dayName} ${day}/${monthNum}/${year} ${hours}:${minutes}:${seconds}`;
  }
  if (mode === "minute" || mode === "hour") {
    return `${dayName} ${day}/${monthNum}/${year} ${hours}:${minutes}`;
  }
  if (mode === "month") {
    return `${monthName} ${fullYear}`;
  }
  if (mode === "year") {
    return `${fullYear}`;
  }
  return `${dayName} ${day}/${monthNum}/${year}`;
};

const parseHierarchy = (flatHierarchy) => {
  if (!flatHierarchy) return [];
  const rootNodes = [];

  // No need to use 'code' (meter_level)
  // Just build a flat list of machines but group by category if they have one
  const entries = Object.entries(flatHierarchy);

  // Identify any "LOAD" or "GRID" or "SOLAR" as main buckets
  // or just show everything linearly as requested "no need to use the code"
  entries.forEach(([id, data]) => {
    const name = data.name || id;
    const category =
      (typeof data === "object" ? data.category : "Unknown") || "Unknown";
    const color = (typeof data === "object" ? data.color : null) || "#808080";

    const node = {
      name,
      id,
      category,
      color,
      hierarchy_path: (typeof data === "object" ? data.hierarchy_path : null) || "",
      is_branch: typeof data === "object" ? !!data.is_branch : false,
      children: [],
      type: category?.toUpperCase() === "MACHINE" ? "machine" : "main",
    };

    rootNodes.push(node);
  });

  return rootNodes;
};

export default useHistoricalData;
